import {
  div,
  span,
  button,
  img,
  figure,
  fnstate,
  input,
  label
} from '@srfnstack/fntags'

/**
 * Reusable full-screen image viewer with zoom and pan
 * @param srcState - fnstate holding the image source URL OR an object with file_path, or null to hide
 * @param onNext - optional callback for carousel next
 * @param onPrev - optional callback for carousel prev
 * @param renderActions - optional function to render extra action buttons: (data) => Node | Node[]
 * @param renderExtraControls - optional function to render extra controls in the top bar: () => Node | Node[]
 * @param getParentSrc - optional function to get the parent image source for diffing: (data) => string | null
 */
export const ImageViewer = (srcState, onNext, onPrev, renderActions, renderExtraControls, getParentSrc) => {
  const zoom = fnstate(1)
  const pan = fnstate({ x: 0, y: 0 })
  const isMasking = fnstate(false)
  const isDiffing = fnstate(false)
  const aspectRatio = fnstate(1)
  const isEraser = fnstate(false)
  const brushSize = fnstate(50)
  const naturalDims = fnstate({ w: 0, h: 0 })
  const viewerBg = fnstate('dark')
  const history = []
  let historyIndex = -1
  let moved = false
  let isOpen = false

  const canvasRef = { current: null }
  const ctxRef = { current: null }
  const cursorRef = { current: null }
  const isDrawing = { current: false }

  const saveHistory = () => {
    if (!canvasRef.current || !ctxRef.current) return
    const imageData = ctxRef.current.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height)
    // Remove any redo history if we draw something new
    if (historyIndex < history.length - 1) {
      history.splice(historyIndex + 1)
    }
    history.push(imageData)
    if (history.length > 20) history.shift() // Limit history
    else historyIndex++
  }

  const undo = () => {
    if (historyIndex > 0) {
      historyIndex--
      ctxRef.current.putImageData(history[historyIndex], 0, 0)
    }
  }

  const redo = () => {
    if (historyIndex < history.length - 1) {
      historyIndex++
      ctxRef.current.putImageData(history[historyIndex], 0, 0)
    }
  }

  const updateCursor = (e) => {
    if (!isMasking() || !cursorRef.current || !canvasRef.current) return
    const rect = canvasRef.current.getBoundingClientRect()
    // brushSize is in image pixels, we need to scale it to screen pixels
    const size = brushSize() * (rect.width / canvasRef.current.width)

    cursorRef.current.style.width = `${size}px`
    cursorRef.current.style.height = `${size}px`
    cursorRef.current.style.left = `${e.clientX}px`
    cursorRef.current.style.top = `${e.clientY}px`
    cursorRef.current.style.display = 'block'
  }

  const hideCursor = () => {
    if (cursorRef.current) cursorRef.current.style.display = 'none'
  }

  const close = () => {
    srcState(null)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight' && onNext && !isMasking()) onNext()
    if (e.key === 'ArrowLeft' && onPrev && !isMasking()) onPrev()
    if (e.key === 'Escape') close()
  }

  const handleWheel = (e) => {
    if (isMasking()) return
    e.preventDefault()
    const delta = e.deltaY * -0.001
    const newZoom = Math.min(Math.max(0.5, zoom() + delta), 10)
    zoom(newZoom)
  }

  const startPan = (e) => {
    if (isMasking() || e.button !== 0 || e.target.classList.contains('diff-resizer')) return
    e.preventDefault()
    moved = false
    const startX = e.clientX - pan().x
    const startY = e.clientY - pan().y

    const movePan = (moveEvent) => {
      if (Math.abs(moveEvent.clientX - (startX + pan().x)) > 5 ||
                 Math.abs(moveEvent.clientY - (startY + pan().y)) > 5) {
        moved = true
      }
      pan({
        x: moveEvent.clientX - startX,
        y: moveEvent.clientY - startY
      })
    }

    const endPan = () => {
      window.removeEventListener('mousemove', movePan)
      window.removeEventListener('mouseup', endPan)
      window.removeEventListener('blur', endPan)
    }

    window.addEventListener('mousemove', movePan)
    window.addEventListener('mouseup', endPan)
    window.addEventListener('blur', endPan)
  }

  const initCanvas = (imgElement) => {
    naturalDims({ w: imgElement.naturalWidth, h: imgElement.naturalHeight })
    aspectRatio(imgElement.naturalWidth / imgElement.naturalHeight)
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    canvas.width = imgElement.naturalWidth
    canvas.height = imgElement.naturalHeight
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    ctxRef.current = ctx
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // Initial state for undo
    history.length = 0
    historyIndex = -1
    saveHistory()

    if (pendingMask) {
      window.__showMaskOverlay(pendingMask)
    }
  }

  const startDrawing = (e) => {
    if (!isMasking() || e.button !== 0) return
    isDrawing.current = true

    const endDrawing = () => {
      if (isDrawing.current) {
        saveHistory()
      }
      isDrawing.current = false
      if (ctxRef.current) ctxRef.current.beginPath()
      window.removeEventListener('mouseup', endDrawing)
      window.removeEventListener('blur', endDrawing)
    }

    window.addEventListener('mouseup', endDrawing)
    window.addEventListener('blur', endDrawing)

    draw(e)
  }

  const stopDrawing = () => {
    if (isDrawing.current) {
      saveHistory()
    }
    isDrawing.current = false
    if (ctxRef.current) ctxRef.current.beginPath()
  }

  const draw = (e) => {
    if (!isDrawing.current || !isMasking() || !ctxRef.current) return
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()

    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    const x = (e.clientX - rect.left) * scaleX
    const y = (e.clientY - rect.top) * scaleY

    if (isEraser()) {
      ctxRef.current.globalCompositeOperation = 'destination-out'
      ctxRef.current.strokeStyle = 'rgba(0,0,0,1)'
    } else {
      ctxRef.current.globalCompositeOperation = 'source-over'
      ctxRef.current.strokeStyle = 'white'
    }
    ctxRef.current.lineWidth = brushSize()
    ctxRef.current.lineTo(x, y)
    ctxRef.current.stroke()
    ctxRef.current.beginPath()
    ctxRef.current.moveTo(x, y)
  }

  const clearMask = () => {
    if (!ctxRef.current || !canvasRef.current) return
    ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    saveHistory()
  }

  // Exposed to window for the ArtworkReviewDetail to call
  window.__getMaskBlob = () => {
    return new Promise((resolve) => {
      if (!canvasRef.current) return resolve(null)
      canvasRef.current.toBlob(resolve, 'image/png')
    })
  }

  window.__toggleMasking = () => {
    isMasking(!isMasking())
    if (!isMasking()) {
      zoom(1)
      pan({ x: 0, y: 0 })
    } else {
      isDiffing(false)
    }
  }

  window.__isMasking = isMasking

  let pendingMask = null
  window.__showMaskOverlay = async (maskPath) => {
    if (!maskPath) return
    isMasking(true)
    if (!ctxRef.current || !canvasRef.current) {
      pendingMask = maskPath
      return
    }

    const maskImg = new window.Image()
    maskImg.crossOrigin = 'anonymous'
    maskImg.src = `/api/image?file=${encodeURIComponent(maskPath)}`
    maskImg.onload = () => {
      ctxRef.current.globalCompositeOperation = 'source-over'
      ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
      ctxRef.current.drawImage(maskImg, 0, 0, canvasRef.current.width, canvasRef.current.height)
      saveHistory()
      pendingMask = null
    }
  }

  // Subscribe to srcState for side effects only (state reset, keydown management).
  // This avoids rebuilding the entire overlay DOM on every image navigation.
  srcState.subscribe(data => {
    if (!data) {
      window.removeEventListener('keydown', handleKeyDown)
      isOpen = false
      zoom(1)
      pan({ x: 0, y: 0 })
      isMasking(false)
      isDiffing(false)
      pendingMask = null
      hideCursor()
      return
    }
    zoom(1)
    pan({ x: 0, y: 0 })
    isMasking(false)
    isDiffing(false)
    naturalDims({ w: 0, h: 0 })
    viewerBg('dark')
    if (!isOpen) {
      window.addEventListener('keydown', handleKeyDown)
      isOpen = true
    }
  })

  const hasActions = !!renderActions

  // Create maskCanvas ONCE — reused across all image navigations
  const maskCanvas = document.createElement('canvas')
  maskCanvas.className = 'absolute inset-0 w-full h-full pointer-events-auto transition-opacity'
  maskCanvas.style.backgroundColor = 'black'
  maskCanvas.style.opacity = '0.6'
  maskCanvas.onmousedown = startDrawing
  maskCanvas.onmousemove = (e) => {
    draw(e)
    updateCursor(e)
  }
  maskCanvas.onmouseup = stopDrawing
  maskCanvas.onmouseenter = updateCursor
  maskCanvas.onmouseleave = () => {
    stopDrawing()
    hideCursor()
  }
  canvasRef.current = maskCanvas

  // Create brushCursor ONCE
  const brushCursor = div({
    class: 'fixed pointer-events-none rounded-full border-2 border-black z-[200] -translate-x-1/2 -translate-y-1/2',
    style: { display: 'none', boxShadow: '0 0 0 1px white' }
  })
  cursorRef.current = brushCursor

  // Build the stable zoom+image container ONCE.
  // zoom.bindAttr, naturalDims.bindAttr, and isMasking.bindAs are now stable
  // single subscriptions instead of accumulating one per image navigation.
  const zoomDiv = div({
    style: zoom.bindAttr(z => ({ transform: `scale(${z})`, transformOrigin: 'center', display: 'block' }))
  },
  img({
    draggable: false,
    src: srcState.bindAttr(data => {
      if (!data) return ''
      return typeof data === 'string' ? data : `/api/image?file=${encodeURIComponent(data.file_path)}`
    }),
    style: naturalDims.bindAttr(nd => {
      const maxH = hasActions ? 0.72 : 0.82
      if (!nd.w) return { maxWidth: '90vw', maxHeight: `${maxH * 100}vh`, objectFit: 'contain' }
      const maxW = window.innerWidth * 0.9
      const maxHpx = window.innerHeight * maxH
      const baseScale = Math.min(maxW / nd.w, maxHpx / nd.h)
      return { width: `${nd.w * baseScale}px`, height: `${nd.h * baseScale}px`, maxWidth: 'none', maxHeight: 'none', objectFit: 'unset' }
    }),
    onload: (e) => initCanvas(e.target),
    onclick: (e) => e.stopPropagation()
  }),
  isMasking.bindAs(masking => {
    maskCanvas.style.display = masking ? 'block' : 'none'
    maskCanvas.style.cursor = masking ? 'none' : 'default'
    return maskCanvas
  })
  )

  // Diff view — ONE stable srcState subscription for the diff content
  const diffViewEl = getParentSrc
    ? srcState.bindAs(data => {
      if (!data) return span()
      const src = typeof data === 'string' ? data : `/api/image?file=${encodeURIComponent(data.file_path)}`
      const pSrc = getParentSrc(data)
      if (!pSrc) return span()
      const maxH = hasActions ? 0.72 : 0.82
      const style = aspectRatio.bindAttr(ratio => ({
        aspectRatio: ratio,
        width: ratio > (90 / (maxH * 100)) ? '90vw' : `calc(${maxH * 100}vh * ${ratio})`,
        height: ratio > (90 / (maxH * 100)) ? `calc(90vw / ${ratio})` : `${maxH * 100}vh`,
        maxWidth: '90vw',
        maxHeight: `${maxH * 100}vh`
      }))
      return figure({
        class: 'diff shadow-2xl',
        style,
        onclick: (e) => e.stopPropagation()
      },
      div({ class: 'diff-item-1' },
        img({
          src,
          class: 'object-cover object-left max-w-none',
          style,
          draggable: false
        })
      ),
      div({ class: 'diff-item-2' },
        img({
          src: pSrc,
          class: 'object-cover max-w-none',
          style,
          draggable: false
        })
      ),
      div({ class: 'diff-resizer' })
      )
    })
    : span()

  // Pan container is stable — pan.bindAttr creates ONE subscription
  const panContainer = div({
    class: viewerBg.bindAttr(bg => `relative shadow-2xl rounded-lg ${bg === 'white' ? 'bg-white' : ''}`),
    style: pan.bindAttr(p => ({
      transform: `translate(${p.x}px, ${p.y}px)`,
      willChange: 'transform'
    }))
  },
  isDiffing.bindAs(diffing => diffing ? diffViewEl : zoomDiv)
  )

  // Build the entire overlay ONCE. Use srcState.bindAttr to show/hide it.
  return div({
    class: srcState.bindAttr(data => data
      ? 'fixed inset-0 z-[100] bg-black/90 flex flex-col select-none overflow-hidden'
      : 'hidden'),
    onclick: () => {
      if (!moved && !isMasking()) close()
      moved = false
    }
  },
  brushCursor,

  // TOP CONTROLS BAR
  div({
    class: 'shrink-0 flex items-center justify-between gap-2 px-3 py-2 z-[110]',
    onclick: (e) => e.stopPropagation()
  },
  div({ class: 'flex items-center gap-2 flex-wrap min-w-0' },
    renderExtraControls ? renderExtraControls() : '',
    // Diff toggle: ONE stable srcState subscription for this section
    getParentSrc
      ? srcState.bindAs(data => {
        const pSrc = data && getParentSrc(data)
        if (!pSrc) return span()
        return label({ class: 'label cursor-pointer gap-2 bg-base-100/20 px-3 py-1 rounded-full' },
          span({ class: 'label-text text-white text-xs' }, 'Diff with original'),
          input({
            type: 'checkbox',
            class: 'toggle toggle-accent toggle-xs',
            checked: isDiffing(),
            onchange: (e) => {
              isDiffing(e.target.checked)
              if (e.target.checked) isMasking(false)
            }
          })
        )
      })
      : '',
    isMasking.bindAs(masking => masking
      ? div({ class: 'flex items-center gap-2 bg-base-300/80 p-2 px-3 rounded-full border border-white/20' },
        label({ class: 'text-white text-xs font-bold uppercase mr-1 hidden sm:block' }, 'Masking'),
        div({ class: 'flex gap-1 mr-2' },
          isEraser.bindAs(eraser => button({
            class: `btn btn-circle btn-sm ${eraser ? 'btn-ghost text-white' : 'btn-primary'}`,
            onclick: () => isEraser(false),
            title: 'Paint Mask'
          }, span({ class: 'material-symbols-rounded !text-[20px]', style: 'font-feature-settings: "liga" !important' }, 'brush'))),
          isEraser.bindAs(eraser => button({
            class: `btn btn-circle btn-sm ${eraser ? 'btn-primary' : 'btn-ghost text-white'}`,
            onclick: () => isEraser(true),
            title: 'Erase Mask'
          }, span({ class: 'material-symbols-rounded !text-[20px]', style: 'font-feature-settings: "liga" !important' }, 'backspace'))),
          div({ class: 'w-px h-6 bg-white/20 mx-1 self-center' }),
          button({
            class: 'btn btn-circle btn-sm btn-ghost text-white',
            onclick: undo,
            title: 'Undo'
          }, span({ class: 'material-symbols-rounded !text-[20px]', style: 'font-feature-settings: "liga" !important' }, 'undo')),
          button({
            class: 'btn btn-circle btn-sm btn-ghost text-white',
            onclick: redo,
            title: 'Redo'
          }, span({ class: 'material-symbols-rounded !text-[20px]', style: 'font-feature-settings: "liga" !important' }, 'redo'))
        ),
        label({ class: 'text-white text-[10px] font-bold uppercase opacity-50 hidden sm:block' }, 'Size'),
        input({
          type: 'range',
          min: '5',
          max: '200',
          value: brushSize(),
          class: 'range range-primary range-xs w-20 sm:w-24 mx-1',
          oninput: (e) => brushSize(parseInt(e.target.value))
        }),
        button({ class: 'btn btn-ghost btn-xs text-white hover:text-error ml-1', onclick: clearMask }, 'Clear')
      )
      : div({ class: 'flex gap-1 bg-base-100/20 p-1 rounded-full' },
        button({ class: 'btn btn-circle btn-xs bg-transparent text-white border-none hover:bg-white/20', onclick: () => zoom(Math.max(0.5, zoom() - 0.2)) }, '−'),
        button({ class: 'btn btn-circle btn-xs bg-transparent text-white border-none hover:bg-white/20', onclick: () => { zoom(1); pan({ x: 0, y: 0 }) } }, '↺'),
        button({ class: 'btn btn-circle btn-xs bg-transparent text-white border-none hover:bg-white/20', onclick: () => zoom(Math.min(10, zoom() + 0.2)) }, '＋')
      )
    ),
    srcState.bindAs(data => {
      const fp = data && (typeof data === 'string' ? data : data.file_path)
      if (!fp || !fp.toLowerCase().endsWith('.png')) return span()
      return div({ class: 'flex gap-1 bg-base-100/20 p-1 rounded-full' },
        viewerBg.bindAs(bg => button({
          class: `btn btn-circle btn-xs border-none ${bg === 'dark' ? 'bg-white/20 text-white' : 'bg-transparent text-white/40 hover:bg-white/20'}`,
          title: 'Dark background',
          onclick: (e) => { e.stopPropagation(); viewerBg('dark') }
        }, span({ class: 'material-symbols-rounded !text-[16px]' }, 'dark_mode'))),
        viewerBg.bindAs(bg => button({
          class: `btn btn-circle btn-xs border-none ${bg === 'white' ? 'bg-white/20 text-white' : 'bg-transparent text-white/40 hover:bg-white/20'}`,
          title: 'White background',
          onclick: (e) => { e.stopPropagation(); viewerBg('white') }
        }, span({ class: 'material-symbols-rounded !text-[16px]' }, 'light_mode')))
      )
    })
  ),
  button({
    class: 'btn btn-circle btn-ghost shrink-0 text-white hover:bg-white/20',
    onclick: (e) => {
      e.stopPropagation()
      close()
    }
  }, '✕')
  ),

  // IMAGE AREA
  div({
    class: 'flex-1 relative flex items-center justify-center min-h-0 cursor-grab active:cursor-grabbing',
    onwheel: handleWheel,
    onmousedown: startPan
  },
  isMasking.bindAs(masking => !masking && onPrev
    ? button({
      class: 'absolute left-2 md:left-8 top-1/2 -translate-y-1/2 btn btn-circle btn-lg bg-base-100/20 text-white border-none z-[120] hover:bg-base-100/40 shadow-2xl',
      onclick: (e) => {
        e.stopPropagation()
        onPrev()
      }
    }, '❮')
    : ''
  ),
  isMasking.bindAs(masking => !masking && onNext
    ? button({
      class: 'absolute right-2 md:right-8 top-1/2 -translate-y-1/2 btn btn-circle btn-lg bg-base-100/20 text-white border-none z-[120] hover:bg-base-100/40 shadow-2xl',
      onclick: (e) => {
        e.stopPropagation()
        onNext()
      }
    }, '❯')
    : ''
  ),
  panContainer
  ),

  // BOTTOM ACTION BAR — ONE stable srcState subscription
  hasActions
    ? srcState.bindAs(data => div({
      class: 'shrink-0 flex flex-wrap justify-center gap-2 px-3 py-3 bg-base-300/60 backdrop-blur-md border-t border-white/10 z-[130]',
      onclick: (e) => e.stopPropagation()
    }, ...(data ? [].concat(renderActions(data)) : [])))
    : ''
  )
}
